import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import styles from "./styles.module.css";
import { useState, useEffect, useCallback } from "react";
import SnackBar from "../../SnackBar";
import { ConfirmDialog } from "../../ConfirmDialog";
import { buscaCategoriasTribPorTipoNegocio } from "../../../services/api-planejamento-tributario.service";
import SnackBarProgresso from "../../SnackBarProgresso";
import useClientPolling from "../../../hooks/useClientPolling";

const ModalEditarVersao = ({
  open,
  onClose,
  onEdit,
  loading = false,
  tipoPrevia = "PIS/COFINS",
  idPrevia = null,
  versaoAtual = null, // Dados da versão que será editada
  dadosPrevia = null, // Dados da prévia para obter razão social
  versoesExistentes = [], // Lista de versões existentes para calcular próximo número
  isProjeto = false, // Se é projeto ou prévia
}) => {
  const loadingState = loading || sseLoading;

  const [nomeVersao, setNomeVersao] = useState("");
  const [categoria, setCategoria] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  // Estados para confirmação
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  // ✅ HOOK Client Polling para criação de versão
  const {
    progresso,
    erro,
    resultado,
    loading: sseLoading,
    criarVersaoComMotor,
    limparEstado,
  } = useClientPolling();

  // ✅ Função para mapear etapas para números
  const mapearEtapa = useCallback((etapa) => {
    const etapas = {
      inicio: 1,
      validacao: 2,
      criacao: 3,
      motores: 4,
      motor_fórmula_cofins: 5,
      motor_fórmula_pis: 6,
      concluido: 7,
    };
    return etapas[etapa] || 1;
  }, []);

  // Função para gerar nome automático da versão
  const gerarNomeVersao = () => {
    if (!dadosPrevia) return "";

    // Obter primeiro nome da razão social
    const razaoSocial = dadosPrevia.razaoSocial || "Empresa";
    const primeiroNome = razaoSocial.split(" ")[0];

    // Obter data atual no formato DD/MM/YYYY
    const dataAtual = new Date().toLocaleDateString("pt-BR");

    // Determinar tipo (Prévia ou Projeto)
    const tipo = isProjeto ? "Projeto" : "Prévia";

    // Calcular próximo número da versão
    // Filtrar versões que não são "Original" (idPreviaVersao !== 0)
    const versoesValidas = versoesExistentes.filter(
      (versao) => versao.idPreviaVersao !== 0 && versao.idPreviaVersao !== null
    );
    const proximoNumero = versoesValidas.length + 1;

    // Gerar nome: Tipo + Primeiro nome + Data + Número da versão
    return `${tipo} ${primeiroNome} ${dataAtual} ${proximoNumero}`;
  };

  // Atualizar nome da versão quando dados da prévia mudarem
  useEffect(() => {
    if (open && dadosPrevia && categoria) {
      const nomeGerado = gerarNomeVersao();
      setNomeVersao(nomeGerado);
    } else if (open && !categoria) {
      setNomeVersao("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dadosPrevia, tipoPrevia, versoesExistentes, categoria]); // gerarNomeVersao é função estável

  // Carregar categorias quando o modal abre
  useEffect(() => {
    if (open) {
      carregarCategorias();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // carregarCategorias é função estável

  // Preencher dados da versão quando ela mudar
  useEffect(() => {
    if (open && versaoAtual) {
      setNomeVersao(versaoAtual.descricao || "");
    }
  }, [versaoAtual, open]);

  const carregarCategorias = async () => {
    setLoadingCategorias(true);

    try {
      const response = await buscaCategoriasTribPorTipoNegocio(tipoPrevia);
      setCategorias(response.categorias || []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao carregar categorias.",
      });
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleSubmit = () => {
    // Validações
    if (!categoria) {
      setObjSnackBar({
        type: "error",
        msg: "Categoria é obrigatória.",
      });
      return;
    }

    // Mostrar modal de confirmação
    setOpenConfirmModal(true);
  };

  // ✅ MODIFICADA: Função para confirmar edição com Client Polling
  const handleConfirmSubmit = async () => {
    try {
      setOpenConfirmModal(false);
      onClose();

      // ✅ USAR CLIENT POLLING PARA RECRIAR VERSÃO
      limparEstado();
      await criarVersaoComMotor({
        tipoPrevia: tipoPrevia,
        descricao: nomeVersao,
        idPrevia: versaoAtual?.idPrevia,
        idCategoria: categoria.idCategoriaTributaria,
        usuCadastro: parseInt(localStorage.getItem("userId") || "1"),
        // ✅ FLAG PARA INDICAR QUE É EDIÇÃO
        isEdicao: true,
        versaoOriginalId: versaoAtual?.idPreviaVersao,
      });

      setNomeVersao("");
      setCategoria(null);
    } catch (error) {
      console.error("❌ ERRO:", error);
    }
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
  };

  const handleClose = () => {
    setNomeVersao("");
    setCategoria(null);
    setObjSnackBar({ type: "", msg: "" });
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <div className={styles.title}>
            <CopyAllIcon sx={{ marginRight: ".5rem" }} />
            Editar Versão
          </div>
        </DialogTitle>
        <DialogContent className={styles.content}>
          <Autocomplete
            value={categoria}
            onChange={(_, value) => {
              setCategoria(value);
            }}
            options={categorias}
            getOptionLabel={(option) => option.descricao || ""}
            fullWidth
            loading={loadingCategorias}
            disabled={loadingCategorias}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                placeholder={
                  loadingCategorias
                    ? "Carregando categorias..."
                    : "Selecione uma categoria"
                }
              />
            )}
          />
          <TextField
            label="Nome da Versão"
            variant="outlined"
            fullWidth
            value={nomeVersao}
            InputProps={{
              readOnly: true,
            }}
            placeholder="Selecione uma categoria para gerar o nome"
          />
          <DialogActions className={styles.containerBtn}>
            <Button
              onClick={handleClose}
              variant="contained"
              className={styles.btnCancelar}
              disabled={loadingState}
            >
              Fechar
            </Button>
            <Button
              variant="contained"
              className={styles.btnSalvar}
              onClick={handleSubmit}
              disabled={!categoria || loadingState}
            >
              {loadingState ? "Editando..." : "Editar"}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação */}
      <ConfirmDialog
        open={openConfirmModal}
        onClose={handleCloseConfirmModal}
        onClickSim={handleConfirmSubmit}
        text="Esta ação irá excluir a versão atual e criar uma nova versão com os dados atualizados. Tem certeza que deseja continuar?"
      />

      {/* ✅ SnackBar de Progresso Client Polling */}
      <SnackBarProgresso
        open={sseLoading || progresso || erro || resultado}
        onClose={() => {
          limparEstado();
          // ✅ REABRIR MODAL APÓS SUCESSO
          if (resultado && resultado.sucesso) {
            setTimeout(() => {
              window.reabrirModalAnalisar?.();
            }, 1000);
          }
        }}
        progressData={(() => {
          const mappedData = {
            titulo: progresso?.mensagem || "Recriando versão...",
            progresso: progresso?.progresso || 0,
            etapaAtual: mapearEtapa(progresso?.etapa) || 1,
            totalEtapas: 7,
            descricao: progresso?.descricao || progresso?.mensagem,
            tipoMotor: tipoPrevia,
            tempoEstimado: progresso?.tempoEstimado || "Calculando...",
            sucesso: erro ? false : resultado ? true : undefined,
            erro: erro ? true : false,
          };
          return mappedData;
        })()}
        showLoading={sseLoading && !erro && !resultado}
      />

      {/* SnackBar */}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalEditarVersao;
